import { FC } from 'react';

import { useSearchParams } from 'react-router-dom';
import leapifyLogo from 'assets/images/Leapify-Logo.svg';
import { type FormikHelpers } from 'formik';
import { cn } from 'utils/helpers';
import dayjs from 'dayjs';
import { TermsPolicy } from 'pages/public/components/common';

import { hero1, icon1, icon2, pop1, pop2 } from '../../assets/images';
import PublicWrapper from '../PublicWrapper';
import RegisterForm from './forms/RegisterForm';
import { useRegister } from './hooks';

export interface REGISTER_FORM {
  emailAddress: string;
  mobileNumber: string;
  firstName: string;
  lastName: string;
  password: string;
  passwordConfirmation: string;
  termsConditions: boolean;
}

const icons = [
  {
    src: hero1,
    alt: 'Hero',
    animate: 'zoom-in',
    className: 'top-[20%] left-[10%]',
  },
  {
    src: icon1,
    alt: 'icon1',
    animate: 'fade-down-right',
    className: 'top-[25%] left-[5%]',
  },
  {
    src: icon2,
    alt: 'icon2',
    animate: 'fade-down-left',
    className: 'top-[40%] right-[-30%]',
  },
  {
    src: pop1,
    alt: 'pop1',
    animate: 'fade-up-left',
    className: 'top-[55%] right-[-20%]',
  },
  {
    src: pop2,
    alt: 'pop2',
    animate: 'fade-up-right',
    className: 'top-[33%] left-[8%]',
  },
];

const Register: FC = () => {
  const { mutate: register, isLoading } = useRegister();
  const [query] = useSearchParams();

  const onSubmit = (
    value: REGISTER_FORM,
    { setSubmitting }: FormikHelpers<REGISTER_FORM>,
  ) => {
    register(
      { ...value, referralCode: query?.get('referralId') },
      {
        onSettled: () => {
          setSubmitting(false);
        },
      },
    );
  };

  return (
    <PublicWrapper
      className={cn(
        'flex-col flex items-center md:justify-center',
        'bg-fill-primary',
      )}
    >
      <div className="grid w-full place-items-center">
        <div
          className={cn(
            'flex',
            'px-6 py-10',
            'max-w-[900px] w-full',
            'gap-x-[77px]',
          )}
        >
          <div className="w-full max-w-[423px] relative">
            <img
              alt="Leapify Logo"
              className="w-[137px] mb-[42px] inline-block mx-auto"
              src={leapifyLogo}
            />
            <div data-aos="fade-up" className="relative z-[999]">
              <h2
                className={cn(
                  'max-w-[282px] mb-[21px]',
                  'text-[36px] leading-[40px] font-semibold',
                )}
              >
                Start your free trial today
              </h2>
              <p className="text-sm mb-[14px] leading-[22px]">
                We create a customized approach for businesses in various
                industries which increase sales, maximize customer satisfaction,
                and takes the guesswork out of advertising on digital platforms.
              </p>
              <RegisterForm onSubmitForm={onSubmit} isLoading={isLoading} />
              <p
                className={cn(
                  'text-center',
                  'text-content text-sm',
                  'pt-2',
                  'block',
                  'md:hidden',
                )}
              >
                {dayjs().format('YYYY')} Terms of Service Privacy Policy
              </p>
            </div>
          </div>
          <div className="relative flex-1 hidden md:block">
            {icons.map(({ src, alt, animate, className }) => (
              <img
                key={Math.random()}
                src={src}
                alt={alt}
                data-aos={animate}
                className={cn('absolute hidden md:block', className)}
              />
            ))}
          </div>
        </div>
        <TermsPolicy className="relative hidden text-center md:block" />
      </div>
    </PublicWrapper>
  );
};

export default Register;
